import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../../services/api";
import { toast } from "react-toastify";
import styles from "./Invoice.module.css";
import { formatLocalDateTime } from "../../utils/datetime";

const AddInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    invoice_id: "",
    internal_transaction_id: "",
    user_id: "",
    user_address: "",
    user_gst_number: "",
    hsn_code: "",
    plan_name: "",
    plan_validity: "",
    invoice_amount: "",
    discount_code: "",
    discount_amount: "",
    invoice_amount_net: "",
    cgst_rate: "",
    cgst: "",
    sgst_rate: "",
    sgst: "",
    igst_rate: "",
    igst: "",
    total_invoice_amount: "",
    payment_method: "",
    service_provider_tid: "",
    created_at: "",
    updated_at: "",
    status: "",
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditMode && location.state?.invoice) {
      setFormData(location.state.invoice);
    } else if (isEditMode) {
      api.get(`/invoice/${id}`).then(res => {
        if (res.status && res.result?.length > 0) {
          setFormData(res.result[0]);
        }
      });
    }
  }, [id, isEditMode, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEditMode) {
        // Placeholder for backend update
        // await api.put(`/invoice/${formData.internal_transaction_id}`, { status: formData.status });
        toast.success("Invoice status updated locally for demo purposes!");
        navigate("/invoice");
      }
    } catch (error) {
      toast.error(error.message || "Failed to save invoice.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => formatLocalDateTime(dateStr, "");

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{isEditMode ? "Manage Invoice" : "View Invoice"}</h1>
        <p className={styles.pageSubtitle}>Review and update status for Transaction ID: {id}</p>
      </div>

      <div className={styles.formCard}>
        <form onSubmit={handleSubmit}>

          <h3 className={styles.sectionHeader}>
            👤 Customer & Plan Details
          </h3>

          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Internal TX ID</label>
              <input
                type="text"
                value={formData.internal_transaction_id || ""}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Invoice ID</label>
              <input
                type="text"
                value={formData.invoice_id || ""}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>User ID</label>
              <input
                type="number"
                value={formData.user_id || ""}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>User Name</label>
              <input
                type="text"
                value={formData.user_name || ""}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>User GST Number</label>
              <input
                type="text"
                name="user_gst_number"
                value={formData.user_gst_number || ""}
                onChange={handleChange}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label>HSN Code</label>
              <input
                type="text"
                value={formData.hsn_code || ""}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Plan Name</label>
              <input
                type="text"
                value={formData.plan_name || ""}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Plan Validity</label>
              <input
                type="text"
                value={formData.plan_validity || ""}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>
          </div>

          <div className={`${styles.formGroup} ${styles.mb32}`}>
            <label>User Address</label>
            <textarea
              value={formData.user_address || ""}
              disabled
              className={`${styles.textarea} ${styles.inputDisabled}`}
            />
          </div>

          <h3 className={styles.sectionHeader}>
            💰 Financial & Tax Breakdown
          </h3>

          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Invoice Amount (Base)</label>
              <input
                type="text"
                value={`₹${formData.invoice_amount || "0"}`}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Discount Code / Amount</label>
              <input
                type="text"
                value={`${formData.discount_code || "None"} (-₹${formData.discount_amount || "0"})`}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Invoice Net Amount</label>
              <input
                type="text"
                value={`₹${formData.invoice_amount_net || "0"}`}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Total Invoice Amount (Gross)</label>
              <input
                type="text"
                value={`₹${formData.total_invoice_amount || "0"}`}
                disabled
                className={`${styles.input} ${styles.inputDisabled} ${styles.textBoldDark}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>CGST Rate / Amount</label>
              <input
                type="text"
                value={`${formData.cgst_rate || "0"}% / ₹${formData.cgst || "0"}`}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>SGST Rate / Amount</label>
              <input
                type="text"
                value={`${formData.sgst_rate || "0"}% / ₹${formData.sgst || "0"}`}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>IGST Rate / Amount</label>
              <input
                type="text"
                value={`${formData.igst_rate || "0"}% / ₹${formData.igst || "0"}`}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>
          </div>

          <h3 className={styles.sectionHeader}>
            💳 Payment Info & Timestamps
          </h3>

          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Payment Method</label>
              <input
                type="text"
                value={formData.payment_method || ""}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Service Provider TID</label>
              <input
                type="text"
                value={formData.service_provider_tid || ""}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Created At</label>
              <input
                type="text"
                value={formatDate(formData.created_at)}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Updated At</label>
              <input
                type="text"
                value={formatDate(formData.updated_at)}
                disabled
                className={`${styles.input} ${styles.inputDisabled}`}
              />
            </div>
          </div>

          <h3 className={styles.sectionHeader}>
            ⚙️ Editable Settings
          </h3>

          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label>Invoice Status</label>
              <select
                name="status"
                value={formData.status || "Completed"}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="SUCCESS">SUCCESS</option>
                <option value="Completed">Completed</option>
                <option value="Failed">Failed</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>

          <div className={styles.actionsContainer}>
            <button
              type="button"
              onClick={() => navigate("/invoice")}
              className={styles.cancelBtn}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={styles.saveBtn}
            >
              {saving ? "Saving..." : "Save Invoice Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInvoice;
